import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Tooltip,
  CircularProgress,
  useTheme,
  IconButton,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import FiltersSidebar from '../components/vehicles/FiltersSidebar';
import VehicleOperations from '../components/vehicles/VehicleOperations';
import GenerateCsvModal from '../components/csv/GenerateCsvModal';
import SyncIcon from '@mui/icons-material/Sync';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RoleBasedAccess, { userHasAccess } from '../services/RoleBasedAccess';
import PaginationFooter from '../components/vehicles/PaginationFooter';
import { useVehicles } from '../hooks/vehicles/useVehicles';
import { VehiclesContext } from '../context/VehiclesContext';
import { defaultFilters } from '../components/vehicles/config';
import VehicleCardList from '../components/vehicles/VehicleCardList';
import VehicleDesktopTable from '../components/vehicles/VehicleDesktopTable';
import AddAssetModal from '../components/asset/AddAssetModal/ModalContainer'
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { Account } from '../state/types';
import { State } from '../state/reducers';
import { useSelector } from 'react-redux';

const Vehicles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const user: Account = useSelector((state: State) => state.accountState.user);

  const vehiclesState = useVehicles();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);


  useEffect(() => {
    if (pollingEnabled) {
      vehiclesState.refetch(); // Fetch immediately on enable
      const interval = setInterval(() => {
        vehiclesState.refetch();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [pollingEnabled, vehiclesState.refetch]);

  useEffect(() => {
    // Only set up listeners and timeouts if polling is active.
    if (pollingEnabled) {
      // Handler for tab visibility
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log("Tab is hidden, disabling polling.");
          setPollingEnabled(false);
        }
      };

      // Set up a 10-minute timeout as a safety net (10 min * 60 sec/min * 1000 ms/sec)
      const pollingTimeout = setTimeout(() => {
        console.log("Polling timeout reached, disabling polling.");
        setPollingEnabled(false);
      }, 10 * 60 * 1000);

      // Add all event listeners
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // The cleanup function is crucial. It runs when polling is turned off (either manually or by one of our handlers).
      return () => {
        console.log("Cleaning up inactivity listeners and timeout.");
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        clearTimeout(pollingTimeout); // <-- This prevents the timeout from firing if the user disables polling early.
      };
    }
  }, [pollingEnabled]);

  const filtersApplied =
    Object.keys(vehiclesState.filters).length > 0 &&
    JSON.stringify(vehiclesState.filters) !== JSON.stringify(defaultFilters);

  const sidebarWidth = isSidebarOpen
    ? (isMobile ? '70vw' : '280px')
    : (isMobile ? '12vw' : '60px');

  const handleAddAssetModalClose = () => {
    setIsAddAssetModalOpen(false); // Close the modal
    vehiclesState.refetch();      // Refetch the data
  };


  return (
    <VehiclesContext.Provider value={vehiclesState}>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {!isMobile && (
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
              width: sidebarWidth,
              transition: 'width 0.3s ease-in-out',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.primary,
              display: 'flex',
              flexDirection: 'column',
              px: isSidebarOpen ? 2 : 0,
              zIndex: 100,
              flexShrink: 0
            }}
          >
            {isSidebarOpen ? (
              <FiltersSidebar
                toggleSidebar={() => setIsSidebarOpen(false)}
              />
            ) : (
              <Box display="flex" flexDirection="column">
                <Tooltip title="Open Filters" placement="right" arrow>
                  <IconButton
                    onClick={() => setIsSidebarOpen(true)}
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: filtersApplied ? '#fff' : theme.palette.text.primary,
                      backgroundColor: filtersApplied ? 'darkred' : 'transparent',
                      '&:hover': {
                        backgroundColor: filtersApplied ? '#a30000' : theme.palette.action.hover,
                        borderColor: filtersApplied ? '#a30000' : theme.palette.text.primary,
                      },
                    }}
                  >
                    <FilterAltOutlinedIcon />
                  </IconButton>
                </Tooltip>


                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <Tooltip title="Add New Asset" placement="right" arrow>
                    <IconButton
                      onClick={() => setIsAddAssetModalOpen(true)}
                      sx={{
                        m: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.text.primary,
                        },
                      }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </RoleBasedAccess>

                <Tooltip title="Generate CSV" placement="right" arrow>
                  <IconButton
                    onClick={() => setIsCsvModalOpen(true)} // It controls the parent's state
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.text.primary,
                      },
                    }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Poll for new vehicles every 60 seconds" placement="right" arrow>
                  <IconButton
                    onClick={() => setPollingEnabled((prev) => !prev)}
                    sx={{
                      m: 1,
                      borderRadius: 5,
                      border: `1px solid ${theme.palette.divider}`,
                      color: pollingEnabled ? '#fff' : theme.palette.text.primary,
                      backgroundColor: pollingEnabled ? 'darkred' : 'transparent',
                      '&:hover': {
                        backgroundColor: pollingEnabled ? '#a30000' : theme.palette.action.hover,
                        borderColor: pollingEnabled ? '#a30000' : theme.palette.text.primary,
                      },
                    }}
                  >
                    {pollingEnabled ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: `calc(100vw - ${sidebarWidth})`,
            padding: 1,
            color: 'text.primary',
            overflow: 'hidden',
          }}
        >

          {vehiclesState.loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                paddingBottom: '20px',
                scrollbarWidth: 'none',        // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none',             // Chrome, Safari
                },
              }}>
                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <VehicleOperations />
                </RoleBasedAccess>
                {isMobile ? <VehicleCardList /> : <VehicleDesktopTable />}
              </Box>

              {/* Pagination + Results Per Page */}
              <PaginationFooter />
            </>
          )}
        </Box>

        {isMobile && (
          <>
            <SpeedDial
              ariaLabel="Vehicle Actions SpeedDial"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              icon={<SpeedDialIcon icon={<FilterListIcon />} openIcon={<CloseIcon />} />}
              onClose={() => setIsSpeedDialOpen(false)}
              onOpen={() => setIsSpeedDialOpen(true)}
              open={isSpeedDialOpen}
              // Style the main FAB based on filtersApplied
              FabProps={{
                sx: {
                  backgroundColor: filtersApplied ? 'darkred' : '#000',
                  color: filtersApplied ? '#fff' : 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: filtersApplied ? '#a30000' : '#000',
                  },
                },
              }}
            >
              <SpeedDialAction
                key="Filters"
                icon={<FilterAltOutlinedIcon color="inherit" />}
                tooltipTitle="Filters"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsFilterDrawerOpen(true);
                }}
              />

              {userHasAccess(user.role, ['Admin', 'Support', 'Auditor']) && (
                <SpeedDialAction
                  key="Add Asset"
                  icon={<AddCircleOutlineIcon />}
                  tooltipTitle="Add New Asset"
                  onClick={() => {
                    setIsSpeedDialOpen(false);
                    setIsAddAssetModalOpen(true);
                  }}
                />
              )}

              <SpeedDialAction
                key="Generate CSV"
                icon={<FileDownloadIcon />}
                tooltipTitle="Generate CSV"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsCsvModalOpen(true); // This now controls the modal
                }}
              />

              <SpeedDialAction
                key="Polling"
                icon={pollingEnabled ? <CircularProgress size={24} color="inherit" /> : <SyncIcon />}
                tooltipTitle={pollingEnabled ? 'Stop Polling' : 'Start Polling'}
                onClick={() => {
                  setPollingEnabled((prev) => !prev);
                }}
              />
            </SpeedDial>

            <Drawer
              anchor="bottom"
              open={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
            >
              <FiltersSidebar toggleSidebar={() => setIsFilterDrawerOpen(false)} />
            </Drawer>
          </>
        )}

        {/* MODALS - Pass state down to them now */}
        <GenerateCsvModal
          open={isCsvModalOpen}
          onClose={() => setIsCsvModalOpen(false)}
        />

        <AddAssetModal
          open={isAddAssetModalOpen}
          onClose={handleAddAssetModalClose}
        />
      </Box>
    </VehiclesContext.Provider >
  );
};

export default Vehicles;
